import React, { useState, useEffect, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import apiClient from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../utils/permissions";
import NumericInput from "./ui/NumericInput";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Design tokens ─────────────────────────────────────────────────────────────

const C = {
  operating:   "#818cf8",
  investing:   "#2dd4bf",
  financing:   "#a78bfa",
  operatingBg: "rgba(129,140,248,0.06)",
  investingBg: "rgba(45,212,191,0.06)",
  financingBg: "rgba(167,139,250,0.06)",
  accent:      "#635bff",
  positive:    "#059669",
  negative:    "#dc2626",
  positiveBg:  "rgba(5,150,105,0.07)",
  negativeBg:  "rgba(220,38,38,0.07)",
  text:        "#0a2540",
  textSec:     "#425466",
  textMuted:   "#94a3b8",
  border:      "#e0e8f0",
  borderLight: "#f1f5f9",
  bg:          "#ffffff",
  bgSubtle:    "#f6f9fc",
};

// ── Constants ─────────────────────────────────────────────────────────────────

const INCOME_CATEGORIES = {
  PROJECT_REVENUE:    "הכנסות פרויקט",
  INVESTMENT_RETURN:  "תשואת השקעה",
  INTEREST_RECEIVED:  "ריבית שהתקבלה",
  ASSET_SALE:         "מכירת נכס",
  OTHER_INCOME:       "הכנסה אחרת",
};
const EXPENSE_CATEGORIES = {
  LOAN_DISBURSEMENT:   "העמדת הלוואה",
  DEBT_SERVICE:        "שירות חוב",
  OPERATING_EXPENSE:   "הוצאה תפעולית",
  INVESTMENT_PURCHASE: "רכישת השקעה",
  OTHER_EXPENSE:       "הוצאה אחרת",
};
const ALL_CATEGORIES = { ...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES };

const SEGMENTS = {
  OPERATING: { label: "מפעילות",  color: C.operating, bg: C.operatingBg },
  INVESTING:  { label: "מהשקעות", color: C.investing,  bg: C.investingBg },
  FINANCING:  { label: "ממימון",  color: C.financing,  bg: C.financingBg },
};

const PERIODS = [
  { label: "חודש",       value: "month"   },
  { label: "רבעון",      value: "quarter" },
  { label: "שנה",        value: "year"    },
  { label: "הכל",        value: "all"     },
  { label: "12M קדימה",  value: "next12"  },
];

const SEGMENT_MAP = {
  PROJECT_REVENUE:    "OPERATING",
  INVESTMENT_RETURN:  "OPERATING",
  INTEREST_RECEIVED:  "OPERATING",
  ASSET_SALE:         "INVESTING",
  OTHER_INCOME:       "OPERATING",
  LOAN_DISBURSEMENT:  "FINANCING",
  DEBT_SERVICE:       "FINANCING",
  OPERATING_EXPENSE:  "OPERATING",
  INVESTMENT_PURCHASE:"INVESTING",
  OTHER_EXPENSE:      "OPERATING",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDateRange(period) {
  const now   = new Date();
  const start = new Date(now);
  if (period === "month")   { start.setDate(1); start.setHours(0, 0, 0, 0); }
  else if (period === "quarter") { start.setMonth(Math.floor(start.getMonth() / 3) * 3, 1); start.setHours(0, 0, 0, 0); }
  else if (period === "year")    { start.setMonth(0, 1); start.setHours(0, 0, 0, 0); }
  else if (period === "next12")  {
    start.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setFullYear(end.getFullYear() + 1);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }
  else return {};
  return { startDate: start.toISOString(), endDate: now.toISOString() };
}

function formatILS(value) {
  if (value == null) return "—";
  const abs = Math.abs(value);
  let formatted;
  if (abs >= 1_000_000_000) formatted = `${(abs / 1_000_000_000).toFixed(1)}B`;
  else if (abs >= 1_000_000) formatted = `${(abs / 1_000_000).toFixed(1)}M`;
  else formatted = abs.toLocaleString("he-IL");
  return `${value < 0 ? "-" : ""}₪${formatted}`;
}

function formatILSFull(value) {
  if (value == null) return "—";
  return new Intl.NumberFormat("he-IL", {
    style: "currency", currency: "ILS", maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("he-IL");
}

/** Group rows by category / source for the summed statement view. */
function groupRows(rows) {
  const groups = {};
  for (const row of rows) {
    let key;
    if      (row.source === "loan_schedule") key = "שירות חוב (לוח סילוקין)";
    else if (row.source === "dividend")      key = "דיבידנדים";
    else if (row.source === "asset_income")  key = "תשואת נכסים";
    else key = ALL_CATEGORIES[row.category] || row.category || "אחר";
    if (!groups[key]) groups[key] = { label: key, amount: 0, count: 0 };
    groups[key].amount += row.amount;
    groups[key].count++;
  }
  return Object.values(groups).sort((a, b) => b.amount - a.amount);
}

// ── Transaction Modal ─────────────────────────────────────────────────────────

const EMPTY_FORM = {
  date:              new Date().toISOString().slice(0, 10),
  type:              "INCOME",
  category:          "PROJECT_REVENUE",
  cfSegment:         "",
  amount:            "",
  currency:          "ILS",
  description:       "",
  projectName:       "",
  // DEBT_SERVICE fields
  relatedLoanId:     "",
  principalAmount:   "",
  interestAmount:    "",
  // ASSET_SALE fields
  relatedAssetHoldingId: "",
  soldUnits:             "",
  soldPercentage:        "",
  isFullDisposition:     false,
  assetSaleMode:         "units", // "units" | "percentage" | "full" — UI-only
};

function TransactionModal({ item, onClose, onSave }) {
  const [form,     setForm]     = useState(item ? { ...EMPTY_FORM, ...item, date: item.date?.slice(0, 10) } : { ...EMPTY_FORM });
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);
  const [loans,    setLoans]    = useState([]);
  const [holdings, setHoldings] = useState([]);

  // Fetch reference data once on mount
  useEffect(() => {
    apiClient.get("/loans?status=active").then(r => setLoans(r.data || [])).catch(() => {});
    apiClient.get("/asset-holdings").then(r => setHoldings((r.data || []).filter(h => h.status !== "SOLD"))).catch(() => {});
  }, []);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleTypeChange = (type) => {
    const defaultCategory = type === "INCOME" ? "PROJECT_REVENUE" : "LOAN_DISBURSEMENT";
    setForm(prev => ({ ...prev, type, category: defaultCategory, cfSegment: SEGMENT_MAP[defaultCategory] || "" }));
  };

  const handleCategoryChange = (category) => {
    setForm(prev => ({ ...prev, category, cfSegment: SEGMENT_MAP[category] || prev.cfSegment }));
  };

  const categoryOptions = form.type === "INCOME" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  // For DEBT_SERVICE: auto-compute total amount from principal + interest
  const isDebtService = form.category === "DEBT_SERVICE";
  const isAssetSale   = form.category === "ASSET_SALE";

  const debtServiceTotal = isDebtService
    ? (Number(form.principalAmount) || 0) + (Number(form.interestAmount) || 0)
    : null;

  const selectedHolding = isAssetSale && form.relatedAssetHoldingId
    ? holdings.find(h => h._id === form.relatedAssetHoldingId)
    : null;
  const isStockHolding = selectedHolding?.assetType === "STOCK";

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const payload = { ...form };
      if (!payload.cfSegment) payload.cfSegment = SEGMENT_MAP[payload.category] || "OPERATING";

      // DEBT_SERVICE: set amount = principal + interest, clean other fields
      if (isDebtService) {
        payload.amount = debtServiceTotal;
        delete payload.relatedAssetHoldingId;
        delete payload.soldUnits;
        delete payload.soldPercentage;
        delete payload.isFullDisposition;
      }

      // ASSET_SALE: set disposition fields based on mode
      if (isAssetSale) {
        const mode = form.assetSaleMode;
        payload.isFullDisposition = mode === "full";
        if (mode !== "percentage") delete payload.soldPercentage;
        if (mode !== "units")      delete payload.soldUnits;
        if (mode === "full") { delete payload.soldUnits; delete payload.soldPercentage; }
        delete payload.relatedLoanId;
      }

      delete payload.assetSaleMode; // UI-only field

      if (item?._id) {
        const { data } = await apiClient.put(`/cashflow/${item._id}`, payload);
        onSave(data);
      } else {
        const { data } = await apiClient.post(`/cashflow`, payload);
        onSave(data);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.modalHeader}>
          <h3 style={{ margin: 0, fontSize: 16, color: C.text }}>{item?._id ? "עריכת תנועה" : "הוספת תנועה"}</h3>
          <button onClick={onClose} style={S.closeBtn}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={S.form}>

          {/* ── Base fields ── */}
          <label style={S.label}>תאריך</label>
          <input style={S.input} type="date" value={form.date} onChange={e => set("date", e.target.value)} required />

          <label style={S.label}>סוג</label>
          <select style={S.input} value={form.type} onChange={e => handleTypeChange(e.target.value)}>
            <option value="INCOME">הכנסה</option>
            <option value="EXPENSE">הוצאה</option>
          </select>

          <label style={S.label}>קטגוריה</label>
          <select style={S.input} value={form.category} onChange={e => handleCategoryChange(e.target.value)}>
            {Object.entries(categoryOptions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>

          <label style={S.label}>סעיף בדוח תזרים</label>
          <select style={S.input} value={form.cfSegment || SEGMENT_MAP[form.category] || "OPERATING"} onChange={e => set("cfSegment", e.target.value)}>
            <option value="OPERATING">מפעילות</option>
            <option value="INVESTING">מהשקעות</option>
            <option value="FINANCING">ממימון</option>
          </select>

          <label style={S.label}>תיאור</label>
          <input style={S.input} value={form.description} onChange={e => set("description", e.target.value)} required />

          {/* ── DEBT_SERVICE fields ── */}
          {isDebtService && (<>
            <label style={S.label}>הלוואה</label>
            <select style={S.input} value={form.relatedLoanId} onChange={e => set("relatedLoanId", e.target.value)} required>
              <option value="">— בחר הלוואה —</option>
              {loans.map(l => (
                <option key={l._id} value={l._id}>
                  {l.lender} / {l.borrowerCompany} {l.remainingPrincipal != null ? `(יתרה: ₪${Number(l.remainingPrincipal).toLocaleString("he-IL")})` : ""}
                </option>
              ))}
            </select>

            <label style={S.label}>החזר קרן (₪)</label>
            <NumericInput style={S.input} min="0" value={form.principalAmount} onChange={e => set("principalAmount", e.target.value)} placeholder="0" />

            <label style={S.label}>תשלום ריבית (₪)</label>
            <NumericInput style={S.input} min="0" value={form.interestAmount} onChange={e => set("interestAmount", e.target.value)} placeholder="0" />

            <div style={{ padding: "8px 12px", background: C.bgSubtle, borderRadius: 6, fontSize: 13, color: C.text, border: `1px solid ${C.border}`, marginBottom: 4 }}>
              סה״כ תשלום: <strong>₪{debtServiceTotal.toLocaleString("he-IL")}</strong>
            </div>
          </>)}

          {/* ── Standard amount (not DEBT_SERVICE) ── */}
          {!isDebtService && (<>
            <label style={S.label}>סכום</label>
            <NumericInput style={S.input} min="0" value={form.amount} onChange={e => set("amount", e.target.value)} required />
          </>)}

          {/* ── ASSET_SALE fields ── */}
          {isAssetSale && (<>
            <label style={S.label}>נכס</label>
            <select style={S.input} value={form.relatedAssetHoldingId} onChange={e => set("relatedAssetHoldingId", e.target.value)} required>
              <option value="">— בחר נכס —</option>
              {holdings.map(h => (
                <option key={h._id} value={h._id}>
                  {h.assetName}{h.symbol ? ` (${h.symbol})` : ""}{h.assetType === "STOCK" ? ` — ${Number(h.usableUnits ?? h.freeUnits ?? h.totalUnits ?? 0).toLocaleString("he-IL")} יח׳ זמינות` : ""}
                </option>
              ))}
            </select>

            {selectedHolding && (<>
              <label style={S.label}>סוג מכירה</label>
              <select style={S.input} value={form.assetSaleMode} onChange={e => set("assetSaleMode", e.target.value)}>
                {isStockHolding && <option value="units">מכירת יחידות</option>}
                {!isStockHolding && <option value="percentage">מכירה חלקית (%)</option>}
                <option value="full">מימוש מלא</option>
              </select>

              {form.assetSaleMode === "units" && isStockHolding && (<>
                <label style={S.label}>יחידות למכירה</label>
                <NumericInput style={S.input} min="1" max={selectedHolding.usableUnits ?? selectedHolding.freeUnits ?? selectedHolding.totalUnits} value={form.soldUnits} onChange={e => set("soldUnits", e.target.value)} required />
                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>סה״כ זמין למכירה: {Number(selectedHolding.usableUnits ?? selectedHolding.freeUnits ?? selectedHolding.totalUnits ?? 0).toLocaleString("he-IL")} יח׳</div>
              </>)}

              {form.assetSaleMode === "percentage" && !isStockHolding && (<>
                <label style={S.label}>אחוז מכירה</label>
                <input style={S.input} type="number" min="1" max="99" value={form.soldPercentage} onChange={e => set("soldPercentage", e.target.value)} required />
              </>)}

              {form.assetSaleMode === "full" && (
                <div style={{ padding: "8px 12px", background: "rgba(220,38,38,0.06)", borderRadius: 6, fontSize: 12, color: C.negative, border: `1px solid rgba(220,38,38,0.2)`, marginBottom: 4 }}>
                  מימוש מלא — הנכס יסומן כ"נמכר" ולא יופיע במאזן
                </div>
              )}
            </>)}
          </>)}

          <label style={S.label}>מטבע</label>
          <input style={S.input} value={form.currency} onChange={e => set("currency", e.target.value)} />

          <label style={S.label}>שם פרויקט (אופציונלי)</label>
          <input style={S.input} value={form.projectName} onChange={e => set("projectName", e.target.value)} />

          {error && <div style={S.errorBox}>{error}</div>}
          <div style={S.modalActions}>
            <button type="button" onClick={onClose} style={S.cancelBtn}>ביטול</button>
            <button type="submit" style={S.saveBtn} disabled={saving}>{saving ? "שומר..." : "שמור"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, net, inflow, outflow, color, subtitle }) {
  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 20px", borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: net >= 0 ? C.positive : C.negative, letterSpacing: "-0.02em" }}>
        {formatILS(net)}
      </div>
      {(inflow != null || outflow != null) && (
        <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 11 }}>
          <span style={{ color: C.positive }}>↑ {formatILS(inflow)}</span>
          <span style={{ color: C.negative }}>↓ {formatILS(outflow)}</span>
        </div>
      )}
      {subtitle && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 5, lineHeight: 1.5 }}>{subtitle}</div>}
    </div>
  );
}

// ── Chart tooltip ─────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12, direction: "rtl", boxShadow: "0 4px 12px rgba(10,37,64,0.1)" }}>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 6 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color || C.text, marginBottom: 2 }}>
          {p.name}: {formatILSFull(p.value)}
        </div>
      ))}
    </div>
  );
}

// ── Cash Flow Chart ───────────────────────────────────────────────────────────

function CfChart({ data }) {
  const [mode, setMode] = useState("combined");

  if (!data?.length) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 260, color: C.textMuted, fontSize: 13 }}>
        אין נתונים לתצוגה
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.textSec }}>תזרים לאורך זמן</span>
        <div style={S.pillToggle}>
          <button style={{ ...S.pill, ...(mode === "combined" ? S.pillActive : {}) }} onClick={() => setMode("combined")}>משולב</button>
          <button style={{ ...S.pill, ...(mode === "split"    ? S.pillActive : {}) }} onClick={() => setMode("split")}>לפי סעיף</button>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 4 }} barCategoryGap="32%">
          <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} vertical={false} />
          <ReferenceLine y={0} stroke={C.border} strokeWidth={1} />
          <XAxis dataKey="month" tick={{ fontSize: 10, fill: C.textMuted }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: C.textMuted }} axisLine={false} tickLine={false} tickFormatter={(v) => formatILS(v)} width={58} />
          <Tooltip content={<ChartTooltip />} />
          {mode === "combined" ? (
            <Bar dataKey="net" name="תזרים נטו" radius={[3, 3, 0, 0]}>
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.net >= 0 ? C.operating : C.negative} fillOpacity={0.85} />
              ))}
            </Bar>
          ) : (
            <>
              <Bar dataKey="operating" name="מפעילות" fill={C.operating} radius={[3, 3, 0, 0]} fillOpacity={0.9} />
              <Bar dataKey="investing"  name="מהשקעות" fill={C.investing}  radius={[3, 3, 0, 0]} fillOpacity={0.9} />
              <Bar dataKey="financing"  name="ממימון"  fill={C.financing}  radius={[3, 3, 0, 0]} fillOpacity={0.9} />
            </>
          )}
        </BarChart>
      </ResponsiveContainer>

      {mode === "split" && (
        <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
          {[["מפעילות", C.operating], ["מהשקעות", C.investing], ["ממימון", C.financing]].map(([lbl, clr]) => (
            <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: C.textSec }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: clr, display: "inline-block" }} />
              {lbl}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CF Statement ──────────────────────────────────────────────────────────────

function StmtSegment({ segKey, data, mode }) {
  const { label, color } = SEGMENTS[segKey];
  const net      = data?.net ?? 0;
  const inflows  = data?.inflows  || [];
  const outflows = data?.outflows || [];
  if (inflows.length === 0 && outflows.length === 0) return null;

  const displayInflows  = mode === "summed" ? groupRows(inflows)  : inflows;
  const displayOutflows = mode === "summed" ? groupRows(outflows) : outflows;

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Segment header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1.5px solid ${color}33` }}>
        <span style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: "0.04em", textTransform: "uppercase" }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: net >= 0 ? C.positive : C.negative }}>{formatILS(net)}</span>
      </div>

      {/* Inflows */}
      {displayInflows.map((row, i) => (
        <div key={`in-${i}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0 4px 10px", borderBottom: `1px solid ${C.borderLight}` }}>
          <span style={{ fontSize: 11, color: C.textSec, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {mode === "summed" ? row.label : row.description}
            {mode === "summed" && row.count > 1 && <span style={{ color: C.textMuted, fontSize: 10, marginRight: 4 }}>×{row.count}</span>}
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.positive, flexShrink: 0, marginRight: 6 }}>+{formatILS(row.amount)}</span>
        </div>
      ))}

      {/* Outflows */}
      {displayOutflows.map((row, i) => (
        <div key={`out-${i}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0 4px 10px", borderBottom: `1px solid ${C.borderLight}` }}>
          <span style={{ fontSize: 11, color: C.textSec, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {mode === "summed" ? row.label : row.description}
            {mode === "summed" && row.count > 1 && <span style={{ color: C.textMuted, fontSize: 10, marginRight: 4 }}>×{row.count}</span>}
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.negative, flexShrink: 0, marginRight: 6 }}>−{formatILS(row.amount)}</span>
        </div>
      ))}
    </div>
  );
}

function CfStatement({ statement }) {
  const [mode, setMode] = useState("summed");
  if (!statement) return null;
  const net = statement.netChange ?? 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.textSec }}>דוח תזרים מזומנים</span>
        <div style={S.pillToggle}>
          <button style={{ ...S.pill, ...(mode === "summed"   ? S.pillActive : {}) }} onClick={() => setMode("summed")}>מסכם</button>
          <button style={{ ...S.pill, ...(mode === "detailed" ? S.pillActive : {}) }} onClick={() => setMode("detailed")}>מפורט</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        <StmtSegment segKey="OPERATING" data={statement.operating} mode={mode} />
        <StmtSegment segKey="INVESTING"  data={statement.investing}  mode={mode} />
        <StmtSegment segKey="FINANCING"  data={statement.financing}  mode={mode} />
      </div>

      {/* Net footer */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1.5px solid ${C.border}`, paddingTop: 10, marginTop: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>שינוי נטו במזומן</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: net >= 0 ? C.positive : C.negative }}>{formatILS(net)}</span>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CashFlowPage({ onUnauthorized, canEdit }) {
  const user = useCurrentUser();
  const canViewSegments = can(user, 'cashFlow.viewSegments');
  const canViewEntries  = can(user, 'cashFlow.viewEntries');
  const canViewCfChart  = can(user, 'cashFlow.viewChart');
  const canViewTable    = can(user, 'cashFlow.viewTable');
  const canAddEntry     = can(user, 'cashFlow.add');
  const canEditEntry    = can(user, 'cashFlow.edit');
  const canDeleteEntry  = can(user, 'cashFlow.remove');
  const [entries,   setEntries]   = useState([]);
  const [statement, setStatement] = useState(null);
  const [period,    setPeriod]    = useState("month");
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editItem,  setEditItem]  = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const range  = getDateRange(period);
      const params = new URLSearchParams(range).toString();
      const [entriesRes, stmtRes] = await Promise.all([
        apiClient.get(`/cashflow?${params}`),
        apiClient.get(`/cashflow/three-statement?${params}`),
      ]);
      setEntries(entriesRes.data);
      setStatement(stmtRes.data);
    } catch (err) {
      if (err.response?.status === 401 && onUnauthorized) onUnauthorized();
      else setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [period, onUnauthorized]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columnDefs = useMemo(() => [
    { field: "date", headerName: "תאריך", width: 110, valueFormatter: ({ value }) => formatDate(value), sort: "desc" },
    {
      field: "type", headerName: "סוג", width: 90,
      cellRenderer: ({ value }) => (
        <span style={{ ...S.badge, background: value === "INCOME" ? C.positiveBg : C.negativeBg, color: value === "INCOME" ? C.positive : C.negative }}>
          {value === "INCOME" ? "הכנסה" : "הוצאה"}
        </span>
      ),
    },
    {
      field: "cfSegment", headerName: "סעיף", width: 110,
      valueFormatter: ({ value, data }) => SEGMENTS[value || SEGMENT_MAP[data?.category] || "OPERATING"]?.label || value,
      cellStyle:      ({ value, data }) => ({ color: SEGMENTS[value || SEGMENT_MAP[data?.category] || "OPERATING"]?.color || C.text, fontWeight: 600, fontSize: 12 }),
    },
    { field: "category", headerName: "קטגוריה", width: 150, valueFormatter: ({ value }) => ALL_CATEGORIES[value] || value },
    { field: "description", headerName: "תיאור", flex: 1 },
    { headerName: "הלוואה מקושרת", width: 140, valueGetter: ({ data }) => data.relatedLoanId?.lender || "—" },
    {
      field: "amount", headerName: "סכום", width: 130, type: "numericColumn",
      valueFormatter: ({ value, data }) => value != null ? `${value.toLocaleString("he-IL")} ${data.currency || "ILS"}` : "—",
    },
    {
      headerName: "פעולות", width: 110, sortable: false, filter: false,
      cellRenderer: ({ data }) => (
        <div style={{ display: "flex", gap: 4 }}>
          {canEditEntry && <button style={S.actionBtn} onClick={() => { setEditItem(data); setShowModal(true); }}>עריכה</button>}
          {canDeleteEntry && <button style={{ ...S.actionBtn, background: C.negativeBg, color: C.negative }} onClick={() => handleDelete(data._id)}>מחק</button>}
        </div>
      ),
    },
  ], [canEditEntry, canDeleteEntry]);

  async function handleDelete(id) {
    if (!window.confirm("למחוק תנועה זו?")) return;
    try {
      await apiClient.delete(`/cashflow/${id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  }

  function handleSave() { setShowModal(false); setEditItem(null); fetchData(); }

  const st = statement;
  const chartData = st?.monthly || [];

  return (
    <div style={S.page}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={S.header}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>תזרים מזומנים</h2>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>מפעילות · מהשקעות · ממימון</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={S.periodTabs}>
            {PERIODS.map((p) => (
              <button
                key={p.value}
                style={{ ...S.periodTab, ...(period === p.value ? S.periodTabActive : {}) }}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {canAddEntry && (
            <button style={S.addBtn} onClick={() => { setEditItem(null); setShowModal(true); }}>
              + הוסף
            </button>
          )}
        </div>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}
      {loading && <div style={S.loadingMsg}>טוען...</div>}

      {!loading && st && (
        <>
          {/* ── KPI row ─────────────────────────────────────────────────────── */}
          {canViewSegments && <div style={S.kpiRow}>
            <KpiCard
              label="מפעילות" color={C.operating}
              net={st.operating?.net ?? 0}
              inflow={(st.operating?.inflows  || []).reduce((s, r) => s + r.amount, 0)}
              outflow={(st.operating?.outflows || []).reduce((s, r) => s + r.amount, 0)}
            />
            <KpiCard
              label="מהשקעות" color={C.investing}
              net={st.investing?.net ?? 0}
              inflow={(st.investing?.inflows  || []).reduce((s, r) => s + r.amount, 0)}
              outflow={(st.investing?.outflows || []).reduce((s, r) => s + r.amount, 0)}
            />
            <KpiCard
              label="ממימון" color={C.financing}
              net={st.financing?.net ?? 0}
              inflow={(st.financing?.inflows  || []).reduce((s, r) => s + r.amount, 0)}
              outflow={(st.financing?.outflows || []).reduce((s, r) => s + r.amount, 0)}
            />
            <KpiCard
              label="שינוי נטו" color={C.accent}
              net={st.netChange ?? 0}
              subtitle={[
                st.loanDebtService?.total > 0 ? `שירות חוב: ${formatILS(st.loanDebtService.total)}` : null,
                st.assetIncome?.total > 0     ? `תשואות: +${formatILS(st.assetIncome.total)}`        : null,
              ].filter(Boolean).join("  ·  ") || null}
            />
          </div>}

          {/* ── Chart + Statement ────────────────────────────────────────────── */}
          <div style={S.mainSection}>
            {canViewCfChart && <div style={S.chartPanel}>
              <CfChart data={chartData} />
            </div>}
            <div style={S.panelDivider} />
            {canViewEntries && <div style={S.stmtPanel}>
              <CfStatement statement={st} />
            </div>}
          </div>
        </>
      )}

      {/* ── Transaction table ────────────────────────────────────────────────── */}
      {canViewTable && <div style={S.tableSection}>
        <div style={S.tableTitleRow}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>תנועות מזומן</span>
          <span style={{ fontSize: 12, color: C.textMuted }}>({entries.length} רשומות)</span>
        </div>
        <div className="ag-theme-alpine ag-theme-premium">
          {loading ? (
            <div style={S.loadingMsg}>טוען...</div>
          ) : (
            <AgGridReact
              theme="legacy"
              rowData={entries}
              columnDefs={columnDefs}
              defaultColDef={{ sortable: true, filter: false, resizable: true, flex: 1, minWidth: 90 }}
              domLayout="autoHeight"
              enableRtl={true}
            />
          )}
        </div>
      </div>}

      {showModal && (
        <TransactionModal
          item={editItem}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  page:   { padding: 24, display: "flex", flexDirection: "column", gap: 16, direction: "rtl" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 },
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 },

  mainSection:  { display: "flex", direction: "ltr", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" },
  chartPanel:   { flex: "0 0 56%", padding: "20px 20px 20px 24px", minWidth: 0 },
  panelDivider: { width: 1, background: C.border, flexShrink: 0 },
  stmtPanel:    { flex: 1, padding: "20px 24px 20px 20px", direction: "rtl", overflowY: "auto", maxHeight: 380 },

  tableSection:  { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" },
  tableTitleRow: { display: "flex", alignItems: "center", gap: 8, padding: "14px 20px", borderBottom: `1px solid ${C.borderLight}` },

  periodTabs:     { display: "flex", background: C.bgSubtle, borderRadius: 7, padding: 3, gap: 1 },
  periodTab:      { background: "none", border: "none", padding: "4px 11px", borderRadius: 5, cursor: "pointer", fontSize: 12, color: C.textSec, fontWeight: 500 },
  periodTabActive:{ background: C.bg, boxShadow: "0 1px 3px rgba(10,37,64,0.10)", fontWeight: 600, color: C.text },

  pillToggle: { display: "flex", background: C.bgSubtle, borderRadius: 6, padding: 2 },
  pill:       { background: "none", border: "none", padding: "3px 10px", borderRadius: 4, cursor: "pointer", fontSize: 11, color: C.textSec, fontWeight: 500 },
  pillActive: { background: C.bg, boxShadow: "0 1px 2px rgba(10,37,64,0.08)", color: C.text, fontWeight: 600 },

  addBtn:    { background: C.accent, color: "#fff", border: "none", borderRadius: 7, padding: "7px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  errorBox:  { background: C.negativeBg, color: C.negative, padding: "10px 14px", borderRadius: 8, fontSize: 13 },
  loadingMsg:{ padding: 32, textAlign: "center", color: C.textMuted, fontSize: 13 },
  badge:     { padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 },
  actionBtn: { background: C.operatingBg, color: C.operating, border: "none", borderRadius: 4, padding: "3px 8px", cursor: "pointer", fontSize: 12 },

  overlay:     { position: "fixed", inset: 0, background: "rgba(10,37,64,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modal:       { background: C.bg, borderRadius: 12, padding: 24, width: 420, maxHeight: "85vh", overflowY: "auto", direction: "rtl", boxShadow: "0 20px 60px rgba(10,37,64,0.18)" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  form:        { display: "flex", flexDirection: "column", gap: 6 },
  label:       { fontSize: 12, color: C.textSec, marginTop: 6, fontWeight: 500 },
  input:       { padding: "7px 10px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 14, width: "100%", boxSizing: "border-box", color: C.text, background: C.bg },
  modalActions:{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 },
  cancelBtn:   { background: C.bgSubtle, color: C.textSec, border: "none", borderRadius: 7, padding: "8px 16px", cursor: "pointer", fontSize: 13 },
  saveBtn:     { background: C.accent, color: "#fff", border: "none", borderRadius: 7, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  closeBtn:    { background: "none", border: "none", cursor: "pointer", fontSize: 18, color: C.textMuted },
};
